import { useRef, useState } from "react"

import { ArrowDownTrayIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon, Cog8ToothIcon } from "@heroicons/react/24/outline"
import { Form, Outlet, useLoaderData, useSearchParams, useSubmit } from "react-router-dom"

import { PagyNav } from "~/src/components"
import { Bee } from "~/src/components/BeeKit"
import {
  StoreItemInventoryTile,
  type StoreItemInventoryTileProps,
} from "~/src/components/Dashboard/StoreItemInventoryTile"
import { EmptyResults } from "~/src/components/EmptyResults"
import { MainHeading } from "~/src/components/MainHeading"
import { Pagy } from "~/src/serializedRecords"

export type StoreItemInventoryItem = {
  id: string
  reorderHref?: string
  settingsHref?: string
} & StoreItemInventoryTileProps

//region Types
export type DashboardStoreInventoryShowProps = {
  meta: Pagy
  name: string
  currentInventoryValueDisplay: string
  storeUrl: string
  items: StoreItemInventoryItem[]
}
//endregion

//region Component

export function DashboardStoreInventoryShow() {
  const data = useLoaderData<DashboardStoreInventoryShowProps>()
  const {
    meta: { pages, next, previous, series = [] },
    name,
    currentInventoryValueDisplay,
    storeUrl,
    items,
  } = data

  const formRef = useRef<HTMLFormElement>(null)
  const submit = useSubmit()
  const [searchParams] = useSearchParams()
  const [searchValue, setSearchValue] = useState<string>(searchParams.get("search") ?? "")

  //region Event Handlers
  //endregion

  //region JSX
  return (
    <div className="mx-6 flex flex-col @container">
      <hgroup className="flex flex-col items-center @2xl:flex-row">
        <MainHeading
          title={`Inventory: ${name}`}
          description={`Current inventory value: ${currentInventoryValueDisplay}`}
          className="ml-0 mr-auto"
        />
        <div className="mb-4 flex w-full justify-between gap-2 @2xl:m-0 @2xl:w-fit @2xl:justify-stretch">
          <Bee.ButtonAnchor
            href="inventory.csv"
            kind="secondary"
            className="flex items-center gap-3 border-0 bg-blueslate-200"
          >
            <ArrowDownTrayIcon className="h-6 w-6" />
            <span>Download .csv</span>
          </Bee.ButtonAnchor>

          <Bee.ButtonAnchor target="_blank" href={storeUrl} className="flex items-center gap-2">
            <ArrowTopRightOnSquareIcon className="h-6 w-6" />
            <span>See store</span>
          </Bee.ButtonAnchor>
        </div>
      </hgroup>

      <section>
        <Form ref={formRef} method="get" className="contents">
          <Bee.Searchbar
            name="search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.currentTarget.value)}
            onClear={() => {
              setSearchValue("")

              if (!formRef.current) return

              const formData = new FormData(formRef.current)
              formData.delete("search")

              submit(formData)
            }}
            className="w-full"
          />
        </Form>
      </section>

      <section className="py-4">{/* Show hidden inventory and Sort Menu will go here */}</section>

      <section className="flex w-full flex-col gap-6">
        {items.length === 0 ? (
          <EmptyResults className="shrink-1 w-full" />
        ) : (
          <div className="flex flex-col items-start justify-start gap-6 self-stretch rounded-lg border bg-white">
            <ul className="w-full divide-y divide-gray-200">
              {items.map((item) => {
                const { id, settingsHref, reorderHref, eGift } = item

                return (
                  <li key={id} className="flex w-full flex-col items-start p-6 @2xl:flex-row">
                    <div className="w-full">
                      <StoreItemInventoryTile {...item} />
                    </div>

                    <div className="flex w-full shrink-0 flex-row justify-between gap-2 pt-4 @2xl:w-fit @2xl:flex-col @2xl:items-start @2xl:pl-4 @2xl:pt-0">
                      {settingsHref && (
                        <Bee.ButtonLink
                          to={settingsHref}
                          className="flex w-full items-center justify-center gap-2 @2xl:w-auto"
                          kind="secondary"
                        >
                          <Cog8ToothIcon className="h-6 w-6" />
                          <span className="text-sm">Settings</span>
                        </Bee.ButtonLink>
                      )}

                      {!eGift && reorderHref && (
                        <Bee.ButtonLink
                          to={reorderHref}
                          className="flex w-full items-center justify-center gap-2 @2xl:w-auto"
                          kind="secondary"
                        >
                          <ArrowPathIcon className="h-6 w-6" />
                          <span className="text-sm">Reorder</span>
                        </Bee.ButtonLink>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </section>

      <Outlet />

      <section className="flex w-full flex-col gap-6">
        {series.length > 0 && pages > 1 && (
          <div className="mb-10 flex justify-center p-8 sm:mb-0">
            <PagyNav previous={previous} next={next} series={series} />
          </div>
        )}
      </section>
    </div>
  )
  //endregion
}
//endregion

//region Private
//endregion
